import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog } from "@/components/ui/dialog";
import RegisterDialog from "./RegisterDialog";
import { UserType } from "@/types/user";
import { useAuth } from "@/hooks/useAuth";
import { AuthContext } from "@/context/AuthContext";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import BankIdLoginDialog from "./BankIdLoginDialog";
import EmailOtpLoginDialog from "./EmailOtpLoginDialog";
import { toast } from "sonner";
import { authLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import NativeAuthOnboarding from "@/components/native/NativeAuthOnboarding";
import {
  buildRedirectAwarePath,
  getSafeRedirectOrDefault,
} from "@/utils/redirectUtils";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";
import type { LoginResponse } from "@/services/auth/authService";
import type { ApiError } from "@/types/carAuctionClient";
import { resetNativeAuthClientState } from "@/integrations/clients/carAuction/carAuctionClient";

interface LoginFormProps {
  isDialog?: boolean;
  defaultUserType?: UserType;
}

const LoginForm = ({ isDialog = false, defaultUserType }: LoginFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const postAuthRedirect = getSafeRedirectOrDefault(redirectParam);
  const TESTING = false; // TODO: remove later, now this is a toggle for all the test login buttons
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [bankIdOpen, setBankIdOpen] = useState(false);
  const [emailOtpOpen, setEmailOtpOpen] = useState(false);
  const [pendingOtpEmail, setPendingOtpEmail] = useState("");
  const [pendingOtpPassword, setPendingOtpPassword] = useState("");
  const [errorFields, setErrorFields] = useState<{
    email: boolean;
    password: boolean;
  }>({
    email: false,
    password: false,
  });
  const [loginError, setLoginError] = useState<string>("");
  const { loginAsync, loginError: authError, isLoggingIn } = useAuth();
  const authContext = useContext(AuthContext);
  const { isNative } = useNativePlatform();

  const { language: lang } = useLanguage();
  const labels = authLabels(lang);

  // Close BankID / register sheets when deep-link returns with an error on /auth
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("bankidError") || sp.get("bankidRegisterError")) {
      setBankIdOpen(false);
      setRegisterOpen(false);
    }
  }, [location.search]);

  // Watch for login errors from the auth hook
  useEffect(() => {
    if (authError) {
      const apiError = authError as unknown as ApiError;
      if (
        apiError.errorCode === "EMAIL_NOT_VERIFIED" ||
        (apiError.details as { requiresEmailOtp?: boolean } | undefined)
          ?.requiresEmailOtp
      ) {
        setLoginError("");
        return;
      }

      if (apiError.errorCode === "REGISTRATION_PENDING") {
        setLoginError(
          labels.error?.registrationPending ||
            "Din kontoförfrågan väntar fortfarande på granskning.",
        );
        return;
      }

      if (apiError.errorCode === "REGISTRATION_REJECTED") {
        setLoginError(
          labels.error?.registrationRejected ||
            "Din kontoförfrågan har avslagits.",
        );
        return;
      }

      // Check if it's a 401 error (invalid credentials)
      const is401Error =
        typeof authError === "object" &&
        authError !== null &&
        "status" in authError &&
        authError.status === 401;

      const isNetworkError =
        typeof authError === "object" &&
        authError !== null &&
        (!("status" in authError) || authError.status === 0);

      if (isNative && isNetworkError) {
        const api = import.meta.env.VITE_API_BASE_URL ?? "API";
        setLoginError(
          `Kan inte nå servern (${api}). Samma WiFi som datorn, korrekt IP i .env, backend på 0.0.0.0:5000.`
        );
      } else {
        setLoginError(
          is401Error ? labels.error?.invalidCredentials : labels.error?.loginError
        );
      }
    } else {
      setLoginError("");
    }
  }, [authError, labels, isNative]);

  const finishLogin = (loginResponse: LoginResponse) => {
    const userResponse = loginResponse.user;

    setEmail("");
    setPassword("");
    setPendingOtpPassword("");

    if (
      userResponse.accountTermsAccepted === false &&
      userResponse.type !== UserType.Admin
    ) {
      navigate(buildRedirectAwarePath("/accept-terms", postAuthRedirect), {
        replace: true,
      });
      toast.info(
        labels.acceptTerms?.toastPleaseAccept ||
          "Please accept our terms to continue.",
        { duration: 5000 },
      );
      return;
    }

    toast.success(
      labels.success?.welcome?.replace("{email}", userResponse.email),
      { duration: 3000 },
    );

    navigate(postAuthRedirect, { replace: true });
  };

  const handleLogin = async () => {
    // Clear previous errors
    setLoginError("");
    setErrorFields({ email: false, password: false });

    const errors = {
      email: email.trim() === "",
      password: password.trim() === "",
    };

    setErrorFields(errors);

    if (errors.email || errors.password) {
      return;
    }

    try {
      const loginResponse = await loginAsync({
        email: email,
        password: password,
        isTest: false,
        emailVerified: true,
      });

      finishLogin(loginResponse);
    } catch (error) {
      const apiError = error as ApiError;
      if (
        apiError?.errorCode === "EMAIL_NOT_VERIFIED" ||
        (apiError?.details as { requiresEmailOtp?: boolean } | undefined)
          ?.requiresEmailOtp
      ) {
        setPendingOtpEmail(
          (apiError?.details as { email?: string } | undefined)?.email ||
            email.trim().toLowerCase(),
        );
        setPendingOtpPassword(password);
        setEmailOtpOpen(true);
        return;
      }
      // Error toast is already shown via the authError useEffect
    }
  };

  const handleEmailOtpVerified = (loginResponse: LoginResponse) => {
    resetNativeAuthClientState();
    authContext?.login(loginResponse.user, loginResponse.companyId);
    finishLogin(loginResponse);
  };

  const handleTestEmailVerificationLogin = async () => {
    // Clear previous errors
    setLoginError("");
    setErrorFields({ email: false, password: false });

    const errors = {
      email: email.trim() === "",
      password: password.trim() === "",
    };

    setErrorFields(errors);

    if (errors.email || errors.password) {
      return;
    }

    // TODO: Use real user data from your authentication system. This is just a mock. Should display error msg if login fails.
    const mockUser = {
      type: UserType.Dealer, // TODO: Mock, fix when backend is ready
      emailVerified: false,
    };

    try {
      const loginResponse = await loginAsync({
        email: email,
        password: password,
        isTest: false,
        emailVerified: false,
      });
      const userResponse = loginResponse.user;

      toast.success(
        labels.success?.welcome?.replace(
          "{email}",
          `${userResponse.firstName} ${userResponse.lastName}`
        ),
        {
          duration: 3000,
        }
      );

      setEmail("");
      setPassword("");

      if (!userResponse.emailVerified) {
        setPendingOtpEmail(userResponse.email);
        setPendingOtpPassword(password);
        setEmailOtpOpen(true);
        return;
      }
      navigate("/dashboard");
    } catch (error) {
      // Error toast is already shown via the authError useEffect
    }
  };

  // TODO: Remove later. For testing admin login, you can create a separate function. This is just a mock.
  const handleTestAdminLogin = async () => {
    // Clear previous errors
    setLoginError("");
    setErrorFields({ email: false, password: false });

    const errors = {
      email: email.trim() === "",
      password: password.trim() === "",
    };

    setErrorFields(errors);

    if (errors.email || errors.password) {
      return;
    }

    const mockUser = {
      type: UserType.Admin,
    };
    try {
      const loginResponse = await loginAsync({
        email: email,
        password: password,
        isTest: true,
      });
      const userResponse = loginResponse.user;

      toast.success(
        labels.success?.welcome?.replace(
          "{email}",
          `${userResponse.firstName} ${userResponse.lastName}`
        ),
        {
          duration: 3000,
        }
      );

      navigate("/dashboard");
    } catch (error) {
      // Error toast is already shown via the authError useEffect
    }
  };

  // TODO: Remove later. For testing dealer login, you can create a separate function. This is just a mock.
  const handleTestDealerLogin = async () => {
    // Clear previous errors
    setLoginError("");
    setErrorFields({ email: false, password: false });

    const errors = {
      email: email.trim() === "",
      password: password.trim() === "",
    };

    setErrorFields(errors);

    if (errors.email || errors.password) {
      return;
    }

    const mockUser = {
      type: UserType.Dealer,
    };
    try {
      const loginResponse = await loginAsync({
        email: email,
        password: password,
        isTest: true,
      });
      const userResponse = loginResponse.user;

      toast.success(
        labels.success?.welcome?.replace(
          "{email}",
          `${userResponse.firstName} ${userResponse.lastName}`
        ),
        {
          duration: 3000,
        }
      );

      navigate("/dashboard");
    } catch (error) {
      // Error toast is already shown via the authError useEffect
    }
  };

  // TODO: Remove later. For testing individual login, you can create a separate function. This is just a mock.
  const handleTestIndividualLogin = async () => {
    // Clear previous errors
    setLoginError("");
    setErrorFields({ email: false, password: false });

    const errors = {
      email: email.trim() === "",
      password: password.trim() === "",
    };

    setErrorFields(errors);

    if (errors.email || errors.password) {
      return;
    }

    const mockUser = {
      type: UserType.Individual,
    };
    try {
      const loginResponse = await loginAsync({
        email: email,
        password: password,
        isTest: true,
      });
      const userResponse = loginResponse.user;

      toast.success(
        labels.success?.welcome?.replace(
          "{email}",
          `${userResponse.firstName} ${userResponse.lastName}`
        ),
        {
          duration: 3000,
        }
      );

      navigate("/dashboard");
    } catch (error) {
      // Error toast is already shown via the authError useEffect
    }
  };

  // TODO: Remove later. For testing user (i.e. företagskunder som inte är Dealer) login, you can create a separate function. This is just a mock.
  const handleTestUserLogin = async () => {
    // Clear previous errors
    setLoginError("");
    setErrorFields({ email: false, password: false });

    const errors = {
      email: email.trim() === "",
      password: password.trim() === "",
    };

    setErrorFields(errors);

    if (errors.email || errors.password) {
      return;
    }

    const mockUser = {
      type: UserType.Business,
    };
    try {
      const loginResponse = await loginAsync({
        email: email,
        password: password,
        isTest: true,
      });
      const userResponse = loginResponse.user;

      toast.success(
        labels.success?.welcome?.replace(
          "{email}",
          `${userResponse.firstName} ${userResponse.lastName}`
        ),
        {
          duration: 3000,
        }
      );

      navigate("/dashboard");
    } catch (error) {
      // Error toast is already shown via the authError useEffect
    }
  };

  const isNativeAuth = isNative && !isDialog;

  return (
    <>
    <form
      className={cn("space-y-4", isNativeAuth && "native-auth-form space-y-3")}
      onSubmit={(e) => {
        e.preventDefault();
        // Nested dialogs (register/company) live beside this form; never treat their Enter as login
        if (registerOpen || forgotOpen || bankIdOpen) return;
        handleLogin();
      }}
    >
      <div className="space-y-2">
        <Label
          htmlFor="email"
          className={errorFields.email ? "text-red-500" : ""}
        >
          {labels.forms?.fields?.email}
        </Label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          placeholder={labels.forms?.fields?.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={errorFields.email ? "border-red-500" : ""}
        />
      </div>
      <div className="space-y-2">
        <Label
          htmlFor="password"
          className={errorFields.password ? "text-red-500" : ""}
        >
          {labels.forms?.fields?.password}
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={cn(
              "pr-10",
              errorFields.password ? "border-red-500" : "",
            )}
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Display login error message */}
      {loginError && <div className="text-red-500 text-sm">{loginError}</div>}

      <div className={cn("space-y-1.5", isNativeAuth && "space-y-2")}>
        <Button
          type="submit"
          className={cn(
            "w-full",
            isNativeAuth &&
              "native-auth-primary-btn min-h-12 h-12 text-[15px] font-semibold shadow-sm",
          )}
          disabled={isLoggingIn || registerOpen}
        >
          {isLoggingIn ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              {labels.buttons?.loggingIn}
            </div>
          ) : (
            labels.buttons?.login
          )}
        </Button>

        <button
          type="button"
          className={cn(
            "text-sm text-muted-foreground underline",
            isNativeAuth && "text-[15px]",
          )}
          onClick={() => setForgotOpen(true)}
        >
          {labels.buttons?.forgotPassword}
        </button>
      </div>

      <div className={cn("relative py-1", isNativeAuth && "py-1.5")}>
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">
            {labels.login?.or || "eller"}
          </span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className={cn(
          "w-full gap-2",
          isNativeAuth &&
            "native-auth-secondary-btn min-h-12 h-12 text-[15px] font-semibold border-border/80",
        )}
        onClick={() => setBankIdOpen(true)}
      >
        <img
          src="/assets/BankID_logo.png"
          alt="BankID"
          className="h-5 w-auto object-contain"
        />
        {labels.login?.bankId?.button || "Logga in med BankID"}
      </Button>

      {/* Only show test buttons when not in dialog */}
      {!isDialog && TESTING && (
        <>
          {/* TODO: Remove, testing purposes only. Logging in as admin: */}
          <Button
            type="button"
            className="w-full bg-red-400 text-xs sm:text-sm"
            onClick={handleTestAdminLogin}
          >
            (TEST) ADMIN
          </Button>

          {/* TODO: Remove, testing purposes only. Logging in as Dealer: */}
          <Button
            type="button"
            className="w-full bg-blue-400 text-xs sm:text-sm"
            onClick={handleTestDealerLogin}
          >
            (TEST) DEALER
          </Button>

          {/* TODO: Remove, testing purposes only. Logging in as Individual: */}
          <Button
            type="button"
            className="w-full bg-green-400 text-xs sm:text-sm"
            onClick={handleTestIndividualLogin}
          >
            (TEST) INDIVIDUAL
          </Button>

          {/* TODO: Remove, testing purposes only. Logging in as User: */}
          <Button
            type="button"
            className="w-full bg-yellow-400 text-xs sm:text-sm"
            onClick={handleTestUserLogin}
          >
            (TEST) USER
          </Button>

          {/* TODO: Remove, testing purposes only. Logging in to test email verification: */}
          <Button
            type="button"
            className="w-full bg-purple-400 text-xs sm:text-sm"
            onClick={handleTestEmailVerificationLogin}
          >
            (TEST) EMAIL VERIFY
          </Button>
        </>
      )}

      <p
        className={cn(
          "pt-1 text-center text-sm text-muted-foreground",
          isNativeAuth && "pt-2 pb-0.5 text-[15px]",
        )}
      >
        {labels.login?.noAccountPrompt || "Har du inget konto?"}{" "}
        <button
          type="button"
          className="font-medium text-foreground underline underline-offset-2"
          onClick={() => setRegisterOpen(true)}
        >
          {labels.buttons?.createAccount}
        </button>
      </p>

      {isNativeAuth && (
        <div className="native-auth-onboarding-block pt-3 mt-1 border-t border-border/70">
          <NativeAuthOnboarding />
        </div>
      )}
    </form>

    <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
      <RegisterDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        defaultUserType={defaultUserType}
      />
    </Dialog>

    <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    <BankIdLoginDialog
      open={bankIdOpen}
      onOpenChange={setBankIdOpen}
      redirect={postAuthRedirect}
    />
    <EmailOtpLoginDialog
      open={emailOtpOpen}
      onOpenChange={setEmailOtpOpen}
      email={pendingOtpEmail}
      password={pendingOtpPassword}
      onVerified={handleEmailOtpVerified}
    />
    </>
  );
};

export default LoginForm;
